import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import ToastrService from '@/shared/services/ToastrService';
import intlShape from '@/shared/utils/intlShape';

import api from '@/shared/api';
import handleHttpError from '@/shared/services/HandleHttpError';
import FormattedListValue from '@/shared/components/FormattedListValue';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import { CanUpdate } from '@/shared/components/security';
import NumberInput from '@/shared/components/input/NumberInput';
import adminpulse from '@/shared/utils/adminpulse';
import YesNoModal from '@/shared/components/YesNoModal';
import { Modal } from 'react-bootstrap';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import Button from '@/shared/components/Button';
import messages from '../hrmMessages';

export default class AbsenceCorrectionModal extends Component {
  state = {
    rows: [],
    correction: 0,
    remark: '',
    balance: 0,
    saving: false,
    hasChanged: false,
  };

  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    hoursInDay: PropTypes.number,
    counter: PropTypes.shape({ id: PropTypes.string }).isRequired,
  };

  static defaultProps = { hoursInDay: 0 };

  reloadData = () => {
    const { counter, intl } = this.props;
    return api.base
      .get(`/api/AbsenceCounter/Details/${counter.id}`)
      .then(({ data }) => {
        const balance = data && data.length > 0 ? data[data.length - 1].balanceAfter : 0;

        this.setState({ rows: data || [], balance });
      })
      .catch(error => handleHttpError(intl)(error));
  };
  componentDidMount() {
    this.reloadData();
  }

  closeModal = () => {
    const { onClose } = this.props;
    const { hasChanged } = this.state;

    onClose(hasChanged);
  };

  addCorrection = () => {
    const { correction, remark } = this.state;
    const { counter, intl } = this.props;
    if ((correction ?? 0) === 0) return;
    this.setState({ saving: true });
    return api.base
      .post('api/AbsenceCounter/Correction', {
        absenceCounterId: counter.id,
        correctionValue: correction,
        remark,
      })
      .then(() =>
        this.reloadData().finally(() => {
          ToastrService.success();

          this.setState({ correction: 0, remark: '', hasChanged: true }, this.closeModal);
        }),
      )
      .catch(error => handleHttpError(intl)(error))
      .finally(() => {
        this.setState({ saving: false });
      });
  };

  removeCorrection = id => {
    const { intl } = this.props;
    this.setState({ saving: true });
    return api.base
      .delete(`api/AbsenceCounter/Correction/${id}`)
      .then(() =>
        this.reloadData().finally(() => {
          ToastrService.success();

          this.setState({ hasChanged: true });
        }),
      )
      .catch(error => handleHttpError(intl)(error))
      .finally(() => {
        this.setState({ saving: false, deleteCorrection: null });
      });
  };

  correctionValueChanged = value => {
    this.setState({ correction: adminpulse.roundAmount(value, 0, 5) });
  };
  render() {
    const { show, intl, hoursInDay } = this.props;
    const { rows, balance, correction, remark, saving, deleteCorrection } = this.state;
    if (!show) return null;
    const numberInputStyle = {
      maxWidth: '150px',
      display: 'inline-block',
      textAlign: 'right',
    };

    return (
      <Modal
        enforceFocus={false} // set this to false so other modals can be shown on top of this one and be editable
        backdrop="static"
        show={show}
        onHide={this.closeModal}
        dialogClassName="modal-xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {intl.formatMessage(messages.AbsenceCorrections)}{' '}
            <ZendeskHelp article={13956867188764} className="margin5L" />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table">
            <thead>
              <tr>
                <th>
                  <FormattedMessage {...messages.Date} />
                </th>
                <th>
                  <FormattedMessage {...messages.Type} />
                </th>
                <th
                  className={hoursInDay ? 'text-center' : 'text-right'}
                  colSpan={hoursInDay ? 2 : 1}
                >
                  <FormattedMessage {...messages.BalanceBefore} />
                </th>
                <th
                  className={hoursInDay ? 'text-center' : 'text-right'}
                  colSpan={hoursInDay ? 2 : 1}
                >
                  <FormattedMessage {...messages.Mutation} />
                </th>
                <th
                  className={hoursInDay ? 'text-center' : 'text-right'}
                  colSpan={hoursInDay ? 2 : 1}
                >
                  <FormattedMessage {...messages.BalanceAfter} />
                </th>
                <th>
                  <FormattedMessage {...messages.Remark} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows?.map(row => (
                <tr key={row.order}>
                  <td>
                    <FormattedDateShort value={row.date} />
                  </td>
                  <td>
                    <FormattedListValue list="AbsenceDetailType" id={row.type} />
                  </td>
                  <td className="text-right">
                    <FormattedNumber value={row.balanceBefore ?? 0} maximumFractionDigits={2} />
                    &nbsp;
                    <FormattedMessage {...messages.Hours} />
                  </td>
                  {hoursInDay ? (
                    <td className="text-right">
                      <FormattedNumber
                        value={(row.balanceBefore ?? 0) / hoursInDay}
                        maximumFractionDigits={2}
                      />
                      &nbsp;
                      <FormattedMessage {...messages.Days} />
                    </td>
                  ) : null}
                  <td className="text-right">
                    <FormattedNumber value={row.mutation ?? 0} maximumFractionDigits={2} />
                    &nbsp;
                    <FormattedMessage {...messages.Hours} />
                  </td>

                  {hoursInDay ? (
                    <td className="text-right">
                      <FormattedNumber
                        value={(row.mutation ?? 0) / hoursInDay}
                        maximumFractionDigits={2}
                      />
                      &nbsp;
                      <FormattedMessage {...messages.Days} />
                    </td>
                  ) : null}
                  <td className="text-right">
                    <FormattedNumber value={row.balanceAfter ?? 0} maximumFractionDigits={2} />
                    &nbsp;
                    <FormattedMessage {...messages.Hours} />
                  </td>
                  {hoursInDay ? (
                    <td className="text-right">
                      <FormattedNumber
                        value={(row.balanceAfter ?? 0) / hoursInDay}
                        maximumFractionDigits={2}
                      />
                      &nbsp;
                      <FormattedMessage {...messages.Days} />
                    </td>
                  ) : null}
                  <td>{row.remark}</td>
                  <td>
                    {row.correctionId ? (
                      <CanUpdate on="AbsenceAndCounters">
                        <a
                          className="btn btn-xs btn-default"
                          title={intl.formatMessage(messages.RemoveCorrection)}
                          onClick={() => this.setState({ deleteCorrection: row.correctionId })}
                        >
                          <i className="fa fa-trash" />
                        </a>
                      </CanUpdate>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
            <CanUpdate on="AbsenceAndCounters">
              <tfoot>
                <tr>
                  <td colSpan={2}>
                    <FormattedMessage {...messages.AddCorrection} />
                  </td>
                  <td className="text-right">
                    <FormattedNumber value={balance ?? 0} maximumFractionDigits={2} />
                    &nbsp;
                    <FormattedMessage {...messages.Hours} />
                  </td>
                  {hoursInDay ? (
                    <td className="text-right">
                      <FormattedNumber
                        value={(balance ?? 0) / hoursInDay}
                        maximumFractionDigits={2}
                      />
                      &nbsp;
                      <FormattedMessage {...messages.Days} />
                    </td>
                  ) : null}
                  <td className="text-right">
                    <NumberInput
                      value={correction}
                      min={(balance ?? 0) * -1}
                      style={numberInputStyle}
                      disabled={saving}
                      placeholder={intl.formatMessage(messages.Hours)}
                      onChange={this.correctionValueChanged}
                    />
                  </td>
                  {hoursInDay ? (
                    <td className="text-right">
                      <NumberInput
                        value={correction / hoursInDay}
                        min={((balance ?? 0) / hoursInDay) * -1}
                        disabled={saving}
                        style={numberInputStyle}
                        placeholder={intl.formatMessage(messages.Days)}
                        onChange={e => this.correctionValueChanged(e * hoursInDay)}
                      />
                    </td>
                  ) : null}
                  <td className="text-right">
                    <FormattedNumber
                      value={(balance ?? 0) + (correction ?? 0)}
                      maximumFractionDigits={2}
                    />
                    &nbsp;
                    <FormattedMessage {...messages.Hours} />
                  </td>
                  {hoursInDay ? (
                    <td className="text-right">
                      <FormattedNumber
                        value={((balance ?? 0) + (correction ?? 0)) / hoursInDay}
                        maximumFractionDigits={2}
                      />
                      &nbsp;
                      <FormattedMessage {...messages.Days} />
                    </td>
                  ) : null}
                  <td>
                    <textarea
                      value={remark}
                      className="form-control vertical-resize"
                      disabled={saving}
                      placeholder={intl.formatMessage(messages.Remark)}
                      onChange={e => this.setState({ remark: e.target.value })}
                      maxLength={3000}
                    />
                  </td>
                  <td />
                </tr>
              </tfoot>
            </CanUpdate>
          </table>
          {hoursInDay ? (
            <div>
              <label>{intl.formatMessage(messages.HoursInDay)}:</label>
              &nbsp;
              <span>{hoursInDay}</span>
            </div>
          ) : null}

          {deleteCorrection ? (
            <YesNoModal
              intl={intl}
              show
              title={intl.formatMessage(messages.Delete)}
              message={intl.formatMessage(messages.DeleteCorrectionMsg)}
              onNo={() =>
                this.setState({
                  deleteCorrection: null,
                })
              }
              onSave={() => this.removeCorrection(deleteCorrection)}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button showProgress onClick={this.closeModal}>
            {intl.formatMessage(messages.Close)}
          </Button>
          <CanUpdate on="AbsenceAndCounters">
            <Button
              onClick={this.addCorrection}
              disabled={saving || (correction ?? 0) === 0}
              btnClassName="btn-inverse"
              showProgress
              name="OkModal-Ok"
            >
              {intl.formatMessage(messages.Save)}
            </Button>
          </CanUpdate>
        </Modal.Footer>
      </Modal>
    );
  }
}
